import type { CSSProperties } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { trackMetaBookingEntry } from '@/src/lib/meta/meta-pixel';
import classes from './SquareCard.module.scss';

export type SquareCardData = {
  image: string;
  title: string;
  description: string;
  link?: string;
  tag?: string;
  buttonLabel?: string;
  tagBg?: string;
  tagText?: string;
};

export const SquareCard = ({ data }: { data: SquareCardData }) => {
  const buttonLabel = data.buttonLabel ?? 'Découvrir';
  const hasLink = Boolean(data.link && data.link.trim() !== '');

  const styleVars: CSSProperties = {
    ...(data.tagBg ? ({ ['--tag-bg' as string]: data.tagBg } as CSSProperties) : {}),
    ...(data.tagText ? ({ ['--tag-text' as string]: data.tagText } as CSSProperties) : {}),
  };

  const content = (
    <div className={classes.media}>
      <Image
        src={data.image}
        alt={data.title}
        fill
        className={classes.image}
        sizes="(max-width: 620px) 100vw, (max-width: 1100px) 50vw, 560px"
      />
      <div className={classes.overlay} aria-hidden="true" />

      <div className={classes.overlayContent}>
        {data.tag && <div className={classes.tag}>{data.tag}</div>}

        <h3 className={classes.title}>{data.title}</h3>
        <p className={classes.desc}>{data.description}</p>

        {hasLink ? (
          <div className={classes.metaRow}>
            <span className={classes.button} aria-hidden="true">
              {buttonLabel} <span className={classes.arrow}>→</span>
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <article
      className={classes.card}
      style={styleVars}
      data-aos="fade-up"
      data-aos-anchor-placement="top-bottom"
      data-aos-easing="ease"
      data-aos-duration="1000"
    >
      {hasLink ? (
        <Link
          href={data.link!.trim()}
          className={classes.link}
          aria-label={data.title}
          onClick={() => trackMetaBookingEntry(data.link!.trim())}
        >
          {content}
        </Link>
      ) : (
        <div className={classes.link} aria-label={data.title}>
          {content}
        </div>
      )}
    </article>
  );
};